/**
 * `?` keyboard cheat sheet: a centred, searchable dialog generated from the shortcut registry, so
 * it can never drift from what the app binds. Grouped by where a key works.
 */
import { SCOPE_TITLES, SHORTCUT_DEFS, type ShortcutDef, shortcutLabels } from '@viko/transcript-ui'
import { Search } from 'lucide-react'
import { useEffect, useId, useMemo, useState } from 'react'
import { Sheet } from './ui.js'

const ORDER = ['global', 'page', 'component', 'dialog'] as const

export function CheatSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('')
  const inputId = useId()
  useEffect(() => {
    if (open) setQ('')
  }, [open])

  const sections = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const match = (d: ShortcutDef) =>
      !needle ||
      `${d.label} ${d.group} ${shortcutLabels(d).join(' ')} ${d.keys.join(' ')}`
        .toLowerCase()
        .includes(needle)
    const out: { title: string; groups: { name: string; defs: ShortcutDef[] }[] }[] = []
    for (const kind of ORDER) {
      const groups = new Map<string, ShortcutDef[]>()
      for (const d of SHORTCUT_DEFS) {
        if (d.scope.split(':')[0] !== kind || !match(d)) continue
        groups.set(d.group, [...(groups.get(d.group) ?? []), d])
      }
      if (groups.size)
        out.push({
          title: SCOPE_TITLES[kind],
          groups: [...groups].map(([name, defs]) => ({ name, defs })),
        })
    }
    return out
  }, [q])
  const count = sections.reduce((n, s) => n + s.groups.reduce((m, g) => m + g.defs.length, 0), 0)

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Keyboard shortcuts"
      subtitle="Single keys work when you aren’t typing. ⌥ and ⌘ keys work anywhere."
      placement="center"
      size="wide"
      shortcutScope="dialog:cheatsheet"
    >
      <div className="grid gap-5 pb-2">
        <label className="field" htmlFor={inputId}>
          <span className="flex items-center gap-1.5">
            <Search size={14} aria-hidden="true" /> Search shortcuts
          </span>
          <input
            id={inputId}
            type="search"
            className="input"
            placeholder="dictate, capture, model…"
            value={q}
            // biome-ignore lint/a11y/noAutofocus: the dialog exists to search it
            autoFocus
            autoComplete="off"
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <p role="status" className="m-0 text-[14px] text-text-3">
          {count === 0 ? `No shortcut matches “${q.trim()}”.` : ''}
        </p>
        {sections.map((s) => (
          <section key={s.title} aria-label={s.title} className="grid gap-3">
            <h3 className="m-0 text-[15px] font-semibold text-text-1">{s.title}</h3>
            {s.groups.map((g) => (
              <div key={g.name} className="grid gap-1">
                <h4 className="m-0 text-[14px] font-medium text-text-2">{g.name}</h4>
                <ul className="m-0 grid list-none gap-0 p-0">
                  {g.defs.map((d) => (
                    <li
                      key={d.id}
                      data-shortcut={d.id}
                      className="flex items-center justify-between gap-4 border-b border-border-subtle py-1.5 text-[14px]"
                    >
                      <span>{d.label}</span>
                      <span className="flex flex-none flex-wrap justify-end gap-1">
                        {shortcutLabels(d).map((k) => (
                          <kbd key={k} className="!ml-0">
                            {k}
                          </kbd>
                        ))}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        ))}
      </div>
    </Sheet>
  )
}
